<script>
import { computed } from "vue";

import { createRubric } from "@/api";
import Breadcrumbs from "../components/global/breadcrumbs.vue";
import RubricFromScratch from "@/components/rubrics/newRubric/rubric_from_scratch.vue";
import ToggleButton from "@/components/global/eg_toggle_button.vue";
import RubricAi from "@/components/rubrics/newRubric/rubric_ai.vue";
import RubricFromTemplate from "@/components/rubrics/newRubric/rubric_from_template.vue";
import RubricAiUpload from "@/components/rubrics/newRubric/rubric_ai_upload.vue";
import fireConfetti from "@/utils/fireConfetti";
import leaveMixin from "@/mixins/leaveMixin";
import { GRADE_LEVELS, INTENSITIES } from "@/constants";
import { LOCAL_STORAGE_NEW_RUBRIC_ID } from "@/components/rubrics/constants";

export default {
  name: "NewRubricView",

  components: {
    RubricFromTemplate,
    RubricAi,
    RubricFromScratch,
    ToggleButton,
    Breadcrumbs,
  },

  // this.showConfirmationLeaveModal
  mixins: [leaveMixin],
  provide() {
    return {
      newRubricSettings: computed(() => this.newRubricSettings),
      clearRubricSettings: () => {
        this.newRubricSettings = {
          selectedGradeLevel: "middleschool",
          selectedLanguageId: 1,
          selectedIntensityIdx: 1,
          selectedCategoryIdx: 0,
        };
        this.dirty = false;
      },
      updateRubricSettings: (key, value) => {
        this.$set(this.newRubricSettings, key, value);
        this.dirty = true;
      },
      newRubricData: {
        grades: this.grades,
        intensities: this.intensities,
        categories: this.categories,
      },
      saveRubricSettings: (settings) => {
        if (typeof settings.gradeLevel === "string") {
          this.$set(
            this.newRubricSettings,
            "selectedGradeLevel",
            settings.gradeLevel
          );
        }
        if (typeof settings.intensityIdx === "number") {
          this.$set(
            this.newRubricSettings,
            "selectedIntensityIdx",
            settings.intensityIdx
          );
        }
        if (typeof settings.languageId === "number") {
          this.$set(
            this.newRubricSettings,
            "selectedLanguageId",
            settings.languageId
          );
        }
        if (typeof settings.categoryIdx === "number") {
          this.$set(
            this.newRubricSettings,
            "selectedCategoryIdx",
            settings.categoryIdx
          );
        }
      },
    };
  },

  props: {
    isRedirectAfterCreated: {
      type: Boolean,
      default: true,
    },
    initialTab: {
      type: String,
      default: "",
    },
  },

  emits: ["input", "close"],

  data() {
    return {
      confirmationOkTitle: "No, keep editing", // override for leaveMixin
      loading: true,
      selectedButton: this.initialTab,
      breadcrumbs: [
        { text: "Rubrics", href: "/rubrics" },
        { text: "New", active: true },
      ],
      newRubricSettings: {},
      grades: GRADE_LEVELS,
      intensities: INTENSITIES,
      // won't ship subjects yet
      // subjects: [{ label: "English" }, { label: "Math" }, { label: "Science" }],
      categories: [
        {
          label: "Essay",
          translationKey: "rubrics.category.essay",
          value: "essay",
        },
        {
          label: "Short Answer",
          translationKey: "rubrics.category.shortAnswer",
          value: "shortAnswer",
        },
        {
          label: "Case Study",
          translationKey: "rubrics.category.caseStudy",
          value: "caseStudy",
        },
      ],
      selectedButtonToComponent: {
        template: RubricFromTemplate,
        ai: RubricAi,
        scratch: RubricFromScratch,
        upload: RubricAiUpload,
      },
      dirty: false,
    };
  },
  methods: {
    handleClose() {
      if (this.$listeners["close"]) {
        this.$emit("close");
      } else {
        this.$router.push("/rubrics");
      }
    },
    async createNewRubric(rubric) {
      const selectedGrade = this.newRubricSettings.selectedGradeLevel;
      const selectedLanguage = this.newRubricSettings.selectedLanguageId;
      const selectedIntensity =
        this.intensities[this.newRubricSettings.selectedIntensityIdx];

      try {
        const rubricData = {
          ...rubric,
          gradeLevel: selectedGrade,
          LanguageId: selectedLanguage,
          intensity: selectedIntensity.value,
        };
        const { data } = await createRubric(rubricData);

        fireConfetti();

        const redirectTo = this.$route.query.redirectTo || "/rubrics";
        const redirectToHasQuery = redirectTo.includes("?");
        this.$showToastSuccess("The rubric was created successfully.", {
          title: "Success!",
        });
        this.dirty = false;
        localStorage.setItem(LOCAL_STORAGE_NEW_RUBRIC_ID, data.id);
        this.$eventBus.$emit("rubric-created", {
          rubric: data,
          isCreating: this.isCreating,
        });

        if (this.isRedirectAfterCreated) {
          await this.$router.push(
            `${redirectTo}${redirectToHasQuery ? "&" : "?"}`
          );
        }

        this.$emit("input", data);
      } catch (error) {
        // API error is handled in the api/errorHandler.js
        if (!error.isAxiosError) {
          this.$showToastError(`Error while creating rubric`);
        }

        console.error(error);
      }
    },
    // override for leaveMixin
    getIsDirty() {
      return this.dirty;
    },
  },
};
</script>

<template>
  <component
    :is="selectedButtonToComponent[selectedButton]"
    :class="`rubric-${selectedButton} new-rubric-view`"
    @createRubric="createNewRubric($event)"
    @close="handleClose"
  />
</template>

<style scoped lang="scss">
.new-rubric-view {
  display: flex;
  min-height: calc(100vh - var(--navbar-height));
  padding: 0;
  .full-width,
  .rubric-upload {
    width: 100%;
  }
  --uppy-dashboard-area-min-height: 134px;

  .uppy-DashboardTab-inner {
    width: 24px;
    height: 24px;
  }
  ::v-deep {
    .uppy-wrapper {
      padding: 1.5rem;
      border-radius: var(--radius-rd-400, 12px);
      border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
      background: var(--color-surface-neutral-cardBackground, #fff);
    }
    .uppy-Dashboard-AddFiles {
      border-radius: var(--radius-rd-400, 12px);
      background: #f0ecf8;
      border: none;
    }
    .uppy-DashboardTab,
    .uppy-Dashboard-AddFiles-list {
      margin: 0;
    }
    .uppy-Dashboard-AddFiles-title,
    .uppy-DashboardTab-name {
      font-size: 12px;
    }
    .form-group {
      legend {
        font-size: var(--typography-font-size-fs-400);
        font-weight: var(--typography-font-weight-fw-600);
        p.small {
          font-size: var(--typography-font-size-fs-300);
          font-weight: var(--typography-font-weight-fw-400);
          color: var(--color-text-neutral-secondary, #566370) !important;
        }
      }
    }
    .upload-content-area {
      min-height: max-content;
    }
  }
}

.rubric-template.step-0 {
  height: 100%;
  flex-wrap: nowrap;
  flex-direction: column;
  overflow: hidden;

  &::v-deep {
    .b-table-sticky-header {
      max-height: 100%;
    }

    & > .col-12 {
      flex: auto;
    }
  }
  &::v-deep .rubric-table-container .rubric-filter {
    top: 0;
  }
}

.create-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin: auto;
}

.big-button {
  width: 188px;
  height: 108px;
}
</style>

<style lang="scss">
.eg-modal .new-rubric-view {
  min-height: auto;
}
</style>
